/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Users Directory & 360° Profile View (Task 11.4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  UserCheck,
  UserX,
  Coins,
  Search,
  RefreshCw,
  Download,
  Eye,
  Shield,
  ShieldAlert,
  LogIn,
  CheckCircle2,
  AlertTriangle,
  X,
  Layers,
  Check,
  CreditCard,
  FileText,
  KeyRound,
  Cpu,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import { DataTable, type ColumnDef, exportToCsv } from '../components/table/DataTable';
import type {
  AdminUserListItem,
  AdminUsersSummary,
  AdminUserDetail,
  AdminUserStatus,
  AdminUserRole,
} from '@sofia/shared-types';
import { AdjustCreditsModal } from './AdjustCreditsModal';
import { ImpersonateUserModal } from './ImpersonateUserModal';

const USD_TO_VND_RATE = 25400;

export const UserDirectoryView: React.FC = () => {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();

  // Filters state
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const pageSize = 20;

  // Modals state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [statusModalUser, setStatusModalUser] = useState<AdminUserListItem | null>(null);
  const [adjustCreditTarget, setAdjustCreditTarget] = useState<{
    workspaceId: string;
    workspaceName: string;
    currentBalance: number;
    userEmail?: string;
  } | null>(null);
  const [impersonateTargetUser, setImpersonateTargetUser] = useState<AdminUserListItem | null>(null);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Currency Formatter
  const formatCurrency = (usdAmount: number) => {
    if (locale === 'vi') {
      const vnd = Math.round(usdAmount * USD_TO_VND_RATE);
      return (
        <div className="flex flex-col text-xs leading-tight">
          <span className="font-semibold text-foreground">
            {vnd.toLocaleString('vi-VN')} ₫
          </span>
          <span className="text-[10px] text-muted-fg font-mono">
            (${usdAmount.toFixed(2)})
          </span>
        </div>
      );
    }
    return (
      <div className="flex flex-col text-xs leading-tight">
        <span className="font-semibold text-foreground">
          ${usdAmount.toFixed(2)}
        </span>
        <span className="text-[10px] text-muted-fg font-mono">
          (~{(Math.round(usdAmount * USD_TO_VND_RATE)).toLocaleString()} ₫)
        </span>
      </div>
    );
  };

  // Format date
  const formatDate = (isoString: string) => {
    try {
      const d = new Date(isoString);
      return (
        <div className="flex flex-col text-xs">
          <span className="text-foreground font-mono">
            {d.toLocaleDateString(locale === 'vi' ? 'vi-VN' : 'en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
          <span className="text-[10px] text-muted-fg font-mono">
            {d.toLocaleTimeString(locale === 'vi' ? 'vi-VN' : 'en-US', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      );
    } catch {
      return isoString;
    }
  };

  // Query users from D1
  const { data, isLoading, isFetching, refetch } = useQuery<{
    success: boolean;
    data: AdminUserListItem[];
    total: number;
    summary: AdminUsersSummary;
  }>({
    queryKey: ['admin-users-list', roleFilter, statusFilter, searchQuery, page],
    queryFn: async () => {
      return adminClient.get('/api/v1/admin/users', {
        role: roleFilter,
        status: statusFilter,
        search: searchQuery.trim() || undefined,
        page,
        limit: pageSize,
      });
    },
    staleTime: 10_000,
  });

  const users = useMemo(() => data?.data || [], [data?.data]);
  const total = data?.total || 0;
  const summary: AdminUsersSummary = data?.summary || {
    totalUsers: 0,
    activeUsers: 0,
    suspendedUsers: 0,
    totalCreditsInCirculation: 0,
  };

  // Status Update Mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      userId,
      status,
      reason,
    }: {
      userId: string;
      status: AdminUserStatus;
      reason?: string;
    }) => {
      return adminClient.patch(`/api/v1/admin/users/${userId}/status`, { status, reason });
    },
    onSuccess: () => {
      showToast('success', t('admin.users.successStatus'));
      queryClient.invalidateQueries({ queryKey: ['admin-users-list'] });
      setStatusModalUser(null);
    },
    onError: (err: any) => {
      showToast('error', err.message || t('admin.users.errorStatus'));
    },
  });

  // Table Columns
  const columns: ColumnDef<AdminUserListItem>[] = useMemo(() => [
    {
      id: 'user',
      header: t('admin.users.colUser'),
      accessorFn: (row) => `${row.fullName} ${row.email}`,
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          {row.avatarUrl ? (
            <img
              src={row.avatarUrl}
              alt={row.fullName}
              className="w-9 h-9 rounded-xl object-cover border border-surface-border shrink-0"
              loading="lazy"
            />
          ) : (
            <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-semibold text-xs shrink-0">
              {row.fullName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-xs text-foreground truncate" title={row.fullName}>
              {row.fullName}
            </span>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[11px] text-muted-fg truncate" title={row.email}>
                {row.email}
              </span>
              {row.emailVerified && (
                <span title="Verified email" className="inline-flex items-center">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'workspace',
      header: t('admin.users.colWorkspace'),
      accessorKey: 'workspaceName',
      cell: ({ row }) => (
        <div className="flex flex-col space-y-0.5">
          <div className="flex items-center gap-1">
            <Layers className="w-3 h-3 text-primary shrink-0" />
            <span className="text-xs font-medium text-foreground truncate max-w-[140px]" title={row.workspaceName}>
              {row.workspaceName}
            </span>
          </div>
          <span className="font-mono text-[10px] text-muted-fg">#{row.workspaceId.slice(0, 8)}</span>
        </div>
      ),
    },
    {
      id: 'role',
      header: t('admin.users.colRole'),
      accessorKey: 'role',
      cell: ({ row }) => {
        const roleConfig: Record<AdminUserRole, { bg: string; label: string }> = {
          super_admin: {
            bg: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
            label: t('admin.users.roleSuperAdmin'),
          },
          admin: {
            bg: 'bg-primary/10 text-primary border-primary/20',
            label: t('admin.users.roleAdmin'),
          },
          user: {
            bg: 'bg-surface-elevated text-muted-fg border-surface-border',
            label: t('admin.users.roleUser'),
          },
        };
        const cfg = roleConfig[row.role] || roleConfig.user;
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.bg}`}>
            {cfg.label}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: t('admin.users.colStatus'),
      accessorKey: 'status',
      cell: ({ row }) => {
        const statusConfig: Record<AdminUserStatus, { bg: string; dot: string; label: string }> = {
          active: {
            bg: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
            dot: 'bg-emerald-500',
            label: t('admin.users.statusActive'),
          },
          suspended: {
            bg: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
            dot: 'bg-amber-500',
            label: t('admin.users.statusSuspended'),
          },
          banned: {
            bg: 'bg-destructive/10 text-destructive border-destructive/20',
            dot: 'bg-destructive',
            label: t('admin.users.statusBanned'),
          },
          pending_verification: {
            bg: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
            dot: 'bg-blue-500',
            label: t('admin.users.statusPending'),
          },
        };
        const cfg = statusConfig[row.status] || statusConfig.active;
        return (
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </span>
        );
      },
    },
    {
      id: 'wallet',
      header: t('admin.users.colWallet'),
      accessorKey: 'walletBalance',
      cell: ({ row }) => (
        <div className="flex flex-col space-y-0.5">
          <div className="flex items-center gap-1 font-semibold text-xs text-foreground">
            <Coins className="w-3 h-3 text-amber-500 shrink-0" />
            <span>{row.walletBalance.toLocaleString()}</span>
          </div>
          <span className="text-[10px] text-muted-fg font-mono">
            Lifetime: {row.lifetimeCredits.toLocaleString()}
          </span>
        </div>
      ),
    },
    {
      id: 'plan',
      header: t('admin.users.colPlan'),
      cell: ({ row }) => (
        <div className="flex flex-col space-y-0.5">
          <span className="text-xs font-medium text-foreground">
            {row.activePlanName || 'Free Starter'}
          </span>
          <div className="flex items-center gap-1 text-[11px] text-muted-fg">
            <span>{t('admin.users.ltv')}:</span>
            <span className="font-semibold text-foreground font-mono">
              ${row.lifetimeValueUsd.toFixed(2)}
            </span>
          </div>
        </div>
      ),
    },
    {
      id: 'joined',
      header: t('admin.users.colJoined'),
      accessorKey: 'createdAt',
      cell: ({ row }) => formatDate(row.createdAt),
    },
    {
      id: 'actions',
      header: t('admin.users.colActions'),
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSelectedUserId(row.id)}
            className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
            title={t('admin.users.viewProfile')}
          >
            <Eye className="w-4 h-4 text-primary" />
          </button>
          <button
            type="button"
            onClick={() => setAdjustCreditTarget({
              workspaceId: row.workspaceId,
              workspaceName: row.workspaceName,
              currentBalance: row.walletBalance,
              userEmail: row.email,
            })}
            className="p-1.5 rounded-lg text-muted-fg hover:text-amber-500 hover:bg-amber-500/10 transition-colors"
            title={t('admin.users.adjustCredits')}
          >
            <Coins className="w-4 h-4 text-amber-500" />
          </button>
          <button
            type="button"
            onClick={() => setStatusModalUser(row)}
            className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
            title={t('admin.users.updateStatus')}
          >
            <Shield className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setImpersonateTargetUser(row)}
            className="p-1.5 rounded-lg text-muted-fg hover:text-amber-500 hover:bg-amber-500/10 transition-colors"
            title={t('admin.users.impersonate')}
          >
            <LogIn className="w-4 h-4 text-amber-500" />
          </button>
        </div>
      ),
    },
  ], [locale, t]);

  // CSV export handler
  const handleExportCsv = () => {
    exportToCsv(
      columns,
      users,
      `users-directory-${new Date().toISOString().slice(0, 10)}.csv`
    );
  };

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-xs font-medium animate-in fade-in slide-in-from-bottom-3 duration-200 ${
            toastMessage.type === 'success'
              ? 'bg-emerald-600 text-white'
              : 'bg-destructive text-white'
          }`}
        >
          {toastMessage.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <AlertTriangle className="w-4 h-4" />
          )}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-surface-border">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-heading font-bold text-foreground">
              {t('admin.users.title')}
            </h1>
            <p className="text-xs text-muted-fg mt-0.5">
              {t('admin.users.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-surface-elevated hover:bg-surface border border-surface-border text-foreground transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin text-primary' : ''}`} />
            <span>{t('admin.users.refresh')}</span>
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={users.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-surface-elevated hover:bg-surface border border-surface-border text-foreground transition-colors disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            <span>{t('admin.users.exportCsv')}</span>
          </button>
        </div>
      </div>

      {/* 4 Telemetry KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* KPI 1: Total Users */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-sm flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
              {t('admin.users.kpiTotal')}
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-heading font-bold text-foreground">
                {summary.totalUsers.toLocaleString()}
              </span>
              <span className="text-[10px] text-muted-fg">accounts</span>
            </div>
            <p className="text-[11px] text-muted-fg">{t('admin.users.kpiTotalSub')}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
            <Users className="w-5 h-5" />
          </div>
        </div>

        {/* KPI 2: Active Accounts */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-sm flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
              {t('admin.users.kpiActive')}
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-heading font-bold text-emerald-500">
                {summary.activeUsers.toLocaleString()}
              </span>
              <span className="text-[10px] text-muted-fg">in good standing</span>
            </div>
            <p className="text-[11px] text-muted-fg">{t('admin.users.kpiActiveSub')}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-500">
            <UserCheck className="w-5 h-5" />
          </div>
        </div>

        {/* KPI 3: Suspended / Banned */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-sm flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
              {t('admin.users.kpiSuspended')}
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-heading font-bold text-destructive">
                {summary.suspendedUsers.toLocaleString()}
              </span>
              <span className="text-[10px] text-muted-fg">restricted</span>
            </div>
            <p className="text-[11px] text-muted-fg">{t('admin.users.kpiSuspendedSub')}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-destructive/10 border border-destructive/20 flex items-center justify-center text-destructive">
            <UserX className="w-5 h-5" />
          </div>
        </div>

        {/* KPI 4: Total Credits in Circulation */}
        <div className="p-4 rounded-2xl bg-surface border border-surface-border shadow-sm flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
              {t('admin.users.kpiCredits')}
            </span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-heading font-bold text-amber-500">
                {summary.totalCreditsInCirculation.toLocaleString()}
              </span>
              <span className="text-[10px] text-muted-fg">credits</span>
            </div>
            <p className="text-[11px] text-muted-fg">{t('admin.users.kpiCreditsSub')}</p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500">
            <Coins className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 p-3 bg-surface rounded-2xl border border-surface-border">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1.5 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
          >
            <option value="all">{t('admin.users.filterAllRoles')}</option>
            <option value="super_admin">{t('admin.users.roleSuperAdmin')}</option>
            <option value="admin">{t('admin.users.roleAdmin')}</option>
            <option value="user">{t('admin.users.roleUser')}</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-1.5 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
          >
            <option value="all">{t('admin.users.filterAllStatuses')}</option>
            <option value="active">{t('admin.users.statusActive')}</option>
            <option value="suspended">{t('admin.users.statusSuspended')}</option>
            <option value="banned">{t('admin.users.statusBanned')}</option>
          </select>
        </div>

        {/* Search Input */}
        <div className="relative w-full md:w-80">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            placeholder={t('admin.users.searchPlaceholder')}
            className="w-full pl-9 pr-8 py-1.5 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg focus:outline-none focus:border-primary transition-colors"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setPage(1);
              }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-fg hover:text-foreground"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* TanStack Data Table */}
      <DataTable
        columns={columns}
        data={users}
        isLoading={isLoading}
        totalCount={total}
        pageIndex={page - 1}
        pageSize={pageSize}
        onPageChange={(p) => setPage(p + 1)}
        isServerSide={true}
        enableSearch={false}
        emptyTitle={t('admin.users.emptyTitle') || 'No Users Found'}
        emptyDescription={t('admin.users.emptyDescription') || 'When creators register or demo data is seeded, active user accounts and workspaces will appear here.'}
      />

      {/* Modal 1: 360-Degree User Profile Modal */}
      {selectedUserId && (
        <UserDetailModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onUpdateStatus={(user) => {
            setSelectedUserId(null);
            setStatusModalUser(user);
          }}
          onAdjustCredits={(user) => {
            setSelectedUserId(null);
            setAdjustCreditTarget({
              workspaceId: user.workspaceId,
              workspaceName: user.workspaceName,
              currentBalance: user.walletBalance,
              userEmail: user.email,
            });
          }}
          onImpersonate={(user) => {
            setSelectedUserId(null);
            setImpersonateTargetUser(user);
          }}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Modal 2: Change Account Status Modal */}
      {statusModalUser && (
        <UpdateUserStatusModal
          user={statusModalUser}
          isPending={updateStatusMutation.isPending}
          onClose={() => setStatusModalUser(null)}
          onConfirm={(status, reason) =>
            updateStatusMutation.mutate({ userId: statusModalUser.id, status, reason })
          }
        />
      )}

      {/* Modal 3: Adjust Workspace Wallet Credits Modal (Task 11.4.2) */}
      {adjustCreditTarget && (
        <AdjustCreditsModal
          isOpen={Boolean(adjustCreditTarget)}
          workspaceId={adjustCreditTarget.workspaceId}
          workspaceName={adjustCreditTarget.workspaceName}
          currentBalance={adjustCreditTarget.currentBalance}
          userEmail={adjustCreditTarget.userEmail}
          onClose={() => setAdjustCreditTarget(null)}
          onSuccess={(res) => {
            queryClient.invalidateQueries({ queryKey: ['admin-users-list'] });
            queryClient.invalidateQueries({ queryKey: ['admin-user-detail'] });
            showToast(
              'success',
              t('admin.users.adjustSuccess').replace(
                '{amount}',
                res.adjustedAmount > 0 ? `+${res.adjustedAmount.toLocaleString()}` : `${res.adjustedAmount.toLocaleString()}`
              )
            );
          }}
        />
      )}

      {/* Modal 4: Impersonate Customer Account Modal (Task 11.4.3) */}
      {impersonateTargetUser && (
        <ImpersonateUserModal
          isOpen={Boolean(impersonateTargetUser)}
          user={impersonateTargetUser}
          onClose={() => setImpersonateTargetUser(null)}
          onSuccess={(email) => {
            showToast(
              'success',
              t('admin.impersonation.successToast').replace('{email}', email)
            );
          }}
        />
      )}
    </div>
  );
};

/**
 * ============================================================================
 * MODAL 1: User 360-Degree Comprehensive Profile
 * ============================================================================
 */
interface UserDetailModalProps {
  userId: string;
  onClose: () => void;
  onUpdateStatus: (user: AdminUserListItem) => void;
  onAdjustCredits: (user: AdminUserListItem) => void;
  onImpersonate: (user: AdminUserListItem) => void;
  formatCurrency: (amount: number) => React.ReactNode;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  userId,
  onClose,
  onUpdateStatus,
  onAdjustCredits,
  onImpersonate,
  formatCurrency,
}) => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<'overview' | 'orders' | 'activity'>('overview');

  const { data, isLoading, error } = useQuery<{ success: boolean; data: AdminUserDetail }>({
    queryKey: ['admin-user-detail', userId],
    queryFn: async () => {
      return adminClient.get(`/api/v1/admin/users/${userId}`);
    },
  });

  const detail = data?.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 backdrop-blur-sm p-4 sm:p-6 animate-in fade-in duration-150">
      <div className="w-full max-w-3xl h-[80vh] rounded-3xl bg-surface border border-surface-border shadow-2xl flex flex-col overflow-hidden">
        {/* Top Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <Eye className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-heading font-bold text-foreground">
                {t('admin.users.modalDetailTitle')}
              </h3>
              <p className="text-[11px] text-muted-fg font-mono">#{userId}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Navigation Tabs */}
        <div className="flex items-center gap-2 px-6 border-b border-surface-border bg-surface shrink-0">
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`py-3 text-xs font-semibold border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-fg hover:text-foreground'
            }`}
          >
            {t('admin.users.tabOverview')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('orders')}
            className={`py-3 text-xs font-semibold border-b-2 transition-colors ${
              activeTab === 'orders'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-fg hover:text-foreground'
            }`}
          >
            {t('admin.users.tabOrders')} ({detail?.recentOrders.length || 0})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('activity')}
            className={`py-3 text-xs font-semibold border-b-2 transition-colors ${
              activeTab === 'activity'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-fg hover:text-foreground'
            }`}
          >
            {t('admin.users.tabActivity')}
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading && (
            <div className="flex items-center justify-center py-20 text-muted-fg">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs">
              {(error as any).message || 'Failed to load user profile'}
            </div>
          )}

          {detail && activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Profile Card Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-surface-elevated border border-surface-border">
                <div className="flex items-center gap-4">
                  {detail.user.avatarUrl ? (
                    <img
                      src={detail.user.avatarUrl}
                      alt={detail.user.fullName}
                      className="w-14 h-14 rounded-2xl object-cover border border-surface-border"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-xl">
                      {detail.user.fullName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h4 className="text-base font-heading font-bold text-foreground">
                      {detail.user.fullName}
                    </h4>
                    <p className="text-xs text-muted-fg">{detail.user.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
                        {detail.user.role}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                        {detail.user.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onImpersonate(detail.user)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-500 transition-colors"
                  >
                    <LogIn className="w-3.5 h-3.5" />
                    <span>{t('admin.users.impersonate')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onUpdateStatus(detail.user)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-surface hover:bg-surface-elevated border border-surface-border text-foreground transition-colors"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    <span>{t('admin.users.updateStatus')}</span>
                  </button>
                </div>
              </div>

              {/* Grid Metrics */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-2xl bg-surface-elevated border border-surface-border space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
                      {t('admin.users.colWallet')}
                    </span>
                    <button
                      type="button"
                      onClick={() => onAdjustCredits(detail.user)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 border border-amber-500/20 transition-colors"
                      title={t('admin.users.adjustCredits')}
                    >
                      <Coins className="w-3 h-3" />
                      <span>{t('admin.users.adjustCredits')}</span>
                    </button>
                  </div>
                  <div className="flex items-baseline gap-1 text-xl font-heading font-bold text-amber-500">
                    <Coins className="w-4 h-4" />
                    <span>{detail.user.walletBalance.toLocaleString()}</span>
                  </div>
                  <span className="text-[10px] text-muted-fg font-mono">
                    Lifetime: {detail.user.lifetimeCredits.toLocaleString()}
                  </span>
                </div>

                <div className="p-4 rounded-2xl bg-surface-elevated border border-surface-border space-y-1">
                  <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
                    {t('admin.users.colPlan')}
                  </span>
                  <div className="text-sm font-heading font-bold text-foreground truncate">
                    {detail.user.activePlanName || 'Free Starter'}
                  </div>
                  <span className="text-[10px] text-muted-fg font-mono uppercase">
                    {detail.user.activePlanType || 'Default'}
                  </span>
                </div>

                <div className="p-4 rounded-2xl bg-surface-elevated border border-surface-border space-y-1">
                  <span className="text-[11px] font-semibold text-muted-fg uppercase tracking-wider">
                    {t('admin.users.ltv')}
                  </span>
                  <div className="text-xl font-heading font-bold text-foreground">
                    ${detail.user.lifetimeValueUsd.toFixed(2)}
                  </div>
                  <span className="text-[10px] text-muted-fg">
                    {detail.user.totalOrdersCount} orders paid
                  </span>
                </div>
              </div>

              {/* Workspace Details */}
              <div className="p-4 rounded-2xl bg-surface-elevated border border-surface-border space-y-3">
                <h5 className="text-xs font-heading font-semibold text-foreground flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-primary" />
                  <span>{t('admin.users.colWorkspace')}</span>
                </h5>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-muted-fg block text-[11px]">Workspace Name</span>
                    <span className="font-medium text-foreground">{detail.user.workspaceName}</span>
                  </div>
                  <div>
                    <span className="text-muted-fg block text-[11px]">Workspace UUID</span>
                    <span className="font-mono text-[11px] text-muted-fg">{detail.user.workspaceId}</span>
                  </div>
                  <div>
                    <span className="text-muted-fg block text-[11px]">Registration Date</span>
                    <span className="font-mono text-muted-fg">{new Date(detail.user.createdAt).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-fg block text-[11px]">Email Verification</span>
                    <span className="font-medium text-emerald-500">
                      {detail.user.emailVerified ? t('admin.users.verified') : t('admin.users.unverified')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {detail && activeTab === 'orders' && (
            <div className="space-y-4">
              {detail.recentOrders.length === 0 ? (
                <div className="text-center py-12 text-muted-fg text-xs space-y-2">
                  <CreditCard className="w-8 h-8 text-muted-fg mx-auto opacity-50" />
                  <p>{t('admin.users.noOrders')}</p>
                </div>
              ) : (
                <div className="divide-y divide-surface-border rounded-2xl border border-surface-border overflow-hidden bg-surface-elevated">
                  {detail.recentOrders.map((ord) => (
                    <div key={ord.id} className="p-4 flex items-center justify-between text-xs">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold text-foreground">#{ord.id}</span>
                          <span
                            className={`px-1.5 py-0.2 rounded text-[10px] font-semibold uppercase ${
                              ord.status === 'paid'
                                ? 'bg-emerald-500/10 text-emerald-500'
                                : 'bg-amber-500/10 text-amber-500'
                            }`}
                          >
                            {ord.status}
                          </span>
                        </div>
                        <p className="text-muted-fg">{ord.planName} &bull; Gateway: {ord.gatewayCode}</p>
                      </div>
                      <div className="text-right">
                        <div>{formatCurrency(ord.amountPaid)}</div>
                        <span className="text-[10px] text-muted-fg font-mono">
                          {new Date(ord.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {detail && activeTab === 'activity' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-5 rounded-2xl bg-surface-elevated border border-surface-border space-y-2 text-center">
                <Cpu className="w-6 h-6 text-primary mx-auto" />
                <span className="text-xs font-semibold text-muted-fg block">
                  {t('admin.users.tokensUsed')}
                </span>
                <span className="text-2xl font-heading font-bold text-foreground block">
                  {detail.stats.totalTokensUsed.toLocaleString()}
                </span>
              </div>

              <div className="p-5 rounded-2xl bg-surface-elevated border border-surface-border space-y-2 text-center">
                <KeyRound className="w-6 h-6 text-amber-500 mx-auto" />
                <span className="text-xs font-semibold text-muted-fg block">
                  {t('admin.users.apiKeysCount')}
                </span>
                <span className="text-2xl font-heading font-bold text-foreground block">
                  {detail.stats.totalApiKeys}
                </span>
              </div>

              <div className="p-5 rounded-2xl bg-surface-elevated border border-surface-border space-y-2 text-center">
                <FileText className="w-6 h-6 text-emerald-500 mx-auto" />
                <span className="text-xs font-semibold text-muted-fg block">
                  {t('admin.users.documentsCount')}
                </span>
                <span className="text-2xl font-heading font-bold text-foreground block">
                  {detail.stats.totalDocuments}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * ============================================================================
 * MODAL 2: Update User Account Status Modal
 * ============================================================================
 */
interface UpdateUserStatusModalProps {
  user: AdminUserListItem;
  isPending: boolean;
  onClose: () => void;
  onConfirm: (status: AdminUserStatus, reason?: string) => void;
}

const UpdateUserStatusModal: React.FC<UpdateUserStatusModalProps> = ({
  user,
  isPending,
  onClose,
  onConfirm,
}) => {
  const { t } = useI18n();
  const [status, setStatus] = useState<AdminUserStatus>(user.status);
  const [reason, setReason] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(status, reason.trim() || undefined);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-md rounded-3xl bg-surface border border-surface-border shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <ShieldAlert className="w-4 h-4" />
            </div>
            <h3 className="text-sm font-heading font-bold text-foreground">
              {t('admin.users.modalStatusTitle')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-xs text-muted-fg leading-relaxed">
            {t('admin.users.modalStatusDesc')}
          </p>

          <div className="p-3.5 rounded-2xl bg-surface-elevated border border-surface-border space-y-1 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">User Account:</span>
              <span className="font-semibold text-foreground">{user.fullName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-fg">Email:</span>
              <span className="text-muted-fg font-mono">{user.email}</span>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.users.statusLabel')}
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as AdminUserStatus)}
              className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
            >
              <option value="active">{t('admin.users.statusActive')}</option>
              <option value="suspended">{t('admin.users.statusSuspended')}</option>
              <option value="banned">{t('admin.users.statusBanned')}</option>
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.users.reasonLabel')}
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('admin.users.reasonPlaceholder')}
              className="w-full px-3 py-2 text-xs rounded-xl bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg focus:outline-none focus:border-primary transition-colors resize-none"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="px-4 py-2 rounded-xl text-xs font-medium text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-primary text-primary-fg hover:bg-primary/90 transition-colors shadow-sm disabled:opacity-50"
            >
              {isPending ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>{t('admin.users.savingStatus')}</span>
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5" />
                  <span>{t('admin.users.saveStatusBtn')}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
